import {
  data,
  type Params,
  redirect,
  UNSAFE_ErrorResponseImpl,
} from 'react-router-dom';
import { toast } from 'sonner';
import z from 'zod';
import { type ClockType, clock } from '@/models/common/clock.ts';
import {
  ArchivedAtBrand,
  CustomerUnreachableAtBrand,
  LeadId,
  LeadScheduledDateBrand,
  MileageSchema,
  NoShowAtBrand,
  Note,
  WorkFinishedAtBrand,
  WorkStartedAtBrand,
} from '@/models/entities/lead.ts';
import { leadService } from '@/services/leadService.ts';

export type ErrorResponseHeaders = {
  'Content-Type': 'application/json';
  Date: string;
  ETag: string;
  'X-Requested-With': 'client-app';
};

export interface ErrorData {
  error: string;
}

export class ErrorResponse extends UNSAFE_ErrorResponseImpl {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly headers: ErrorResponseHeaders,
    public readonly data: ErrorData,
  ) {
    super(status, statusText, data, false);
  }

  private static simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0;
    }
    return `W/"${Math.abs(hash).toString(16)}"`;
  }

  static validationError(errorMessage: string) {
    const status = 400;
    const statusText = errorMessage;
    const now = new Date().toUTCString();
    const ETag = ErrorResponse.simpleHash(errorMessage + now);
    const headers: ErrorResponseHeaders = {
      'Content-Type': 'application/json',
      Date: now,
      ETag: ETag,
      'X-Requested-With': 'client-app',
    };

    const data = { error: errorMessage };

    return new ErrorResponse(status, statusText, headers, data);
  }
}

const ApiCall = z.function({
  input: [LeadId, z.custom<ClockType>(), z.custom<FormData>()],
  output: z.promise(z.custom<Response>()),
});

export const ActionSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    apiCall: ApiCall,
    form: z.string().optional(),
    successMessage: z.string(),
    redirect: z.string().optional(),
  })
  .transform((original) => ({
    ...original,
    action: async (formData: FormData, params: Params, requestUrl?: URL) => {
      const response = await original.apiCall(
        LeadId.parse(params.leadId),
        clock,
        formData,
      );

      if (response.ok) {
        toast(original.successMessage);
        if (original.redirect) {
          const redirectUrl = new URL(original.redirect, 'http://localhost');
          if (requestUrl?.search) {
            redirectUrl.search = requestUrl.search;
          }

          return redirect(`${redirectUrl.pathname}${redirectUrl.search}`);
        }
      }

      return response;
    },
  }));

type Action = z.infer<typeof ActionSchema>;

const Actions = {
  ScheduleAppointment: ActionSchema.parse({
    key: 'ScheduleAppointment',
    label: 'SCHEDULE APPOINTMENT',
    successMessage: 'Appointment scheduled',
    apiCall: async (leadId: LeadId, _: ClockType, formData: FormData) => {
      const scheduledDate = formData.get('scheduledDate');
      if (typeof scheduledDate === 'string' && scheduledDate.trim() === '') {
        return ErrorResponse.validationError('You need to select a date.');
      }
      return await leadService.patchWorkflow(leadId, {
        scheduledDate: LeadScheduledDateBrand.parse(scheduledDate),
      });
    },
    form: 'CalendarForm',
  }),
  Reschedule: ActionSchema.parse({
    key: 'Reschedule',
    label: 'RESCHEDULE',
    successMessage: 'Appointment rescheduled',
    apiCall: async (leadId: LeadId, _: ClockType, formData: FormData) => {
      const scheduledDate = formData.get('scheduledDate');

      if (!scheduledDate) {
        return ErrorResponse.validationError('You need to select a date.');
      }

      return await leadService.patchWorkflow(leadId, {
        scheduledDate: LeadScheduledDateBrand.parse(scheduledDate),
      });
    },
  }),
  MarkInRepair: ActionSchema.parse({
    key: 'MarkInRepair',
    label: 'MARK IN REPAIR',
    successMessage: 'Work started',
    apiCall: async (leadId: LeadId, clock: ClockType) => {
      return await leadService.patchWorkflow(leadId, {
        workStartedAt: WorkStartedAtBrand.parse(clock.getInstant().toString()),
      });
    },
  }),
  MarkAsExecuted: ActionSchema.parse({
    key: 'MarkAsExecuted',
    label: 'MARK AS EXECUTED',
    successMessage: 'Work finished',
    apiCall: async (leadId: LeadId, clock: ClockType, formData: FormData) => {
      if (formData.get('hasOdometer') !== 'true') {
        return ErrorResponse.validationError('Odometer reading is required');
      }
      return await leadService.patchWorkflow(leadId, {
        workFinishedAt: WorkFinishedAtBrand.parse(
          clock.getInstant().toString(),
        ),
      });
    },
  }),
  Archive: ActionSchema.parse({
    key: 'Archive',
    label: 'ARCHIVE',
    successMessage: 'Lead archived',
    redirect: '/leads',
    apiCall: async (leadId: LeadId, clock: ClockType) => {
      return await leadService.patchWorkflow(leadId, {
        archivedAt: ArchivedAtBrand.parse(clock.getInstant().toString()),
      });
    },
  }),
  MarkNoShow: ActionSchema.parse({
    key: 'MarkNoShow',
    label: 'MARK NO-SHOW',
    successMessage: 'Marked as no-show',
    redirect: '/leads',
    apiCall: async (leadId: LeadId, clock: ClockType) => {
      return await leadService.patchWorkflow(leadId, {
        noShowAt: NoShowAtBrand.parse(clock.getInstant().toString()),
      });
    },
  }),
  MarkUnreachable: ActionSchema.parse({
    key: 'MarkUnreachable',
    label: 'MARK UNREACHABLE',
    successMessage: 'Marked as unreachable',
    redirect: '/leads',
    apiCall: async (leadId: LeadId, clock: ClockType) => {
      return await leadService.patchWorkflow(leadId, {
        customerUnreachableAt: CustomerUnreachableAtBrand.parse(
          clock.getInstant().toString(),
        ),
      });
    },
  }),
  UpdateNote: ActionSchema.parse({
    key: 'UpdateNote',
    label: 'SAVE',
    successMessage: 'Note updated',
    apiCall: async (leadId: LeadId, _: ClockType, formData: FormData) => {
      const note = formData.get('note');
      const result = Note.safeParse(note);

      if (!result.success) {
        return data(
          { error: 'Could not pare note', details: result.error, value: note },
          { status: 400 },
        );
      }

      return await leadService.updateNote(leadId, result.data);
    },
  }),
  UpdateOdometer: ActionSchema.parse({
    key: 'UpdateOdometer',
    label: 'SAVE',
    successMessage: 'Odometer updated',
    apiCall: async (leadId: LeadId, _: ClockType, formData: FormData) => {
      const value = formData.get('odometerValue');
      const unit = formData.get('odometerUnit');
      const result = MileageSchema.safeParse({
        value: value ? Number(value) : undefined,
        unit,
      });

      if (!result.success) {
        return ErrorResponse.validationError('Could not parse odometer');
      }

      return await leadService.updateOdometer(leadId, result.data);
    },
  }),
  getByKey: (key: string) => Actions[key as keyof typeof Actions] as Action,
} as const;
export default Actions;
